/// <reference types="vite/client" />
import type {MainDataRecord, Calendar, CalendarInfo, LifeEvent} from './types';
import {showError, showQuestion, showWarning, stripSuffix} from "./utils";
import dayjs from 'dayjs';
import {DataSyncManager} from "./DataSyncManager.ts";

const MIN_AGE = 1;
const MAX_AGE = 200;

export const DEFAULT_CALENDAR_TITLE = "My Life"

export class DataManager {
	private mainData: MainDataRecord = { id: '', selectedCalendarId: '', calendarIds: [], calendarPanelIsOpen: false }  //reflects server version
    private selectedCalendarId: string = ''  //always points to existing calendar (if there is one), unlike mainData.selectedCalendarId, which may point to calendars that have not yet been synchronized
    private calendars: Calendar[] = []
	private dayEvents: (LifeEvent | null)[] = []  //reflects the current Calendar, should be changed on Calendar selection
	private syncManager: DataSyncManager;
    private conflictUserChoice: boolean|null = null
    private hasDataStatus: boolean|"unknown" = "unknown"

	public onMainDataLoaded?: (hasData: boolean) => void
	public onCalendarListChanged?: () => void
	public onSelectedCalendarChanged?: () => void
    public onTogglePanel?: (expanded: boolean) => void

    public askConflict = (respond: (keepLocalChanges: boolean) => void) => {
        showQuestion("Conflicting changes on the server (parallel editing on another device)", {
            "Keep local changes": () => {
                respond(true)
            },
            "Load server version": () => {
                respond(false)
            }
        })
    }

	constructor(syncManager?: DataSyncManager) {
        this.syncManager = syncManager || new DataSyncManager(stripSuffix(import.meta.env.BASE_URL, '/') + "/api")
        this.syncManager.showError = showError;
        this.syncManager.showWarning = showWarning;
        this.syncManager.onConflict = (id, respond) => {
            if (id == this.mainData.id) {
                return respond(false)
            }
            if (this.conflictUserChoice !== null) {
                return respond(this.conflictUserChoice)
            }
            this.askConflict((keepLocalChanges: boolean) => {
                this.conflictUserChoice = keepLocalChanges
                respond(keepLocalChanges)
            })
        }
        this.syncManager.onSyncFinished = () => {
            this.conflictUserChoice = null
        }
        // Actual calendar list and selected calendar value depend on the loaded calendar records so we need to take into account all the currently loaded data on any update
        this.syncManager.onRecordLoaded = (_id, _data) => this.applyChangedRecordsFromServer()
        this.syncManager.onRecordDeleted = (_id) => this.applyChangedRecordsFromServer()
	}

	public hasData(): boolean {
		return this.calendars.length > 0;
	}

    public initialLoad(): Promise<boolean> {
        const res = this.syncManager.initialLoad()
        this.applyChangedRecordsFromServer()
        return res
    }

    private applyChangedRecordsFromServer()
    {
        function isMainDataRecord(data: any) {
            return (data as MainDataRecord).calendarIds != null
        }

        const prevPanelIsOpen = this.mainData.calendarPanelIsOpen

        const allData = this.syncManager.getAllRecords()
        const mainData = Object.values(allData).find(it => isMainDataRecord(it)) as MainDataRecord
        if (mainData) {
            this.mainData = mainData
            this.calendars = mainData.calendarIds.map(id => allData[id] as Calendar).filter(it => it)
            const allIds = new Set(mainData.calendarIds)
            for (let cal of Object.values(allData))
                if (!isMainDataRecord(cal) && !allIds.has(cal.id)) {
                    this.calendars.push(cal as Calendar)
                }

            this.selectedCalendarId = mainData.selectedCalendarId
            if (!this.calendars.some(c => c.id === mainData.selectedCalendarId)) {
                this.selectedCalendarId = this.calendars[0]?.id || ''
            }
        } else {
            this.mainData = { id: '', selectedCalendarId: '', calendarIds: [], calendarPanelIsOpen: false }
            this.calendars = []
            this.selectedCalendarId = ''
        }

        const hasData = this.hasData()
        if (hasData != this.hasDataStatus) {
            this.hasDataStatus = hasData
            this.onMainDataLoaded && this.onMainDataLoaded(hasData)
        }
        if (this.hasData()) {
            this.buildDayEventsArray()  //todo could detect actual changes to optimize these calls
            this.onCalendarListChanged!()
            this.onSelectedCalendarChanged!()
            if (prevPanelIsOpen !== this.mainData.calendarPanelIsOpen) {
                this.onTogglePanel && this.onTogglePanel(this.mainData.calendarPanelIsOpen)
            }
        }
    }

	private saveMainDataRecord() {
        this.mainData.selectedCalendarId = this.selectedCalendarId
        this.mainData.calendarIds = this.calendars.map(it => it.id)
        if (this.mainData.id) {
            this.syncManager.updateRecord(this.mainData)
        }
        else {
            this.syncManager.createNewRecord(this.mainData)
        }
	}

    public createAppData(birthdate: string, age: number): boolean {
        if (this.hasData()) {
            throw new Error('Illegal state: account already exists')
        }
        if (!DataManager.isValidBirthDate(birthdate)) {
            showWarning('Invalid birthdate');
            return false
        }
        if (!DataManager.isValidAge(age)) {
            showWarning(`Invalid age: must in range ${MIN_AGE} .. ${MAX_AGE}`);
            return false
        }

        return this.createNewCalendar(DEFAULT_CALENDAR_TITLE, birthdate, age)
    }

    //    ----    Account manipulation    ----

    public isLoggedIn(): boolean {
        return this.syncManager.isLoggedIn()
    }

    public signUp(login: string, password: string): Promise<boolean> {
        return this.syncManager.signUp(login, password);
    }

    public getUserName() {
        return this.syncManager.getUserName()
    }

    public login(login: string, password: string): Promise<boolean> {
        return this.syncManager.login(login, password, true);
    }

    public changePassword(oldPassword: string, newPassword: string): Promise<boolean> {
        return this.syncManager.changePassword(oldPassword, newPassword);
    }

    public exit(): Promise<boolean> {
        return this.syncManager.exit();
    }

    //    ----    Calendar manipulation    ----

    public getCalendars(): Calendar[] {
        return this.calendars
    }

	private saveCalendar(calendar: Calendar) {
        this.syncManager.updateRecord(calendar)
	}

	public getSelectedCalendarInfo(): CalendarInfo {
		const calendar = this.getSelectedCalendarOrError()
		const birthDate = dayjs(calendar.birthdate, 'YYYY-MM-DD')
		const endDate = birthDate.add(calendar.age, 'year')
		return {
			id: calendar.id,
			title: calendar.title,
			birthDate: birthDate,
			endDate: endDate,
			age: calendar.age,
			totalDays: endDate.diff(birthDate, 'day')
		};
	}

	public getSelectedCalendarId(): string {
		return this.selectedCalendarId;
	}

	public setSelectedCalendarId(id: string): void {
		this.selectedCalendarId = id
		this.buildDayEventsArray()
		this.onCalendarListChanged!()
		this.onSelectedCalendarChanged!()
		this.saveMainDataRecord()
	}

	private getSelectedCalendar(): Calendar|undefined {
		return this.calendars.find(c => c.id === this.selectedCalendarId)
	}

	private getSelectedCalendarOrError(): Calendar {
		const calendar = this.getSelectedCalendar()
		if (!calendar)  throw new Error("Calendar is not selected")
		return calendar
	}

	public toggleCalendarPanel(isOpen: boolean) {
        if (isOpen === this.mainData.calendarPanelIsOpen)  return
		this.mainData.calendarPanelIsOpen = isOpen
		this.saveMainDataRecord()
	}

	public isCalendarPanelOpen() {
		return this.mainData.calendarPanelIsOpen
	}

	private buildDayEventsArray(calendar?: Calendar): void {
		if (calendar == null)  calendar = this.getSelectedCalendar()
		if (calendar == null)  return
		this.dayEvents = [];
		for (let event of calendar.events) {
			for (let i = 0; i < event.length; i++) {
				const dayIndex = event.index + i;
				this.dayEvents[dayIndex] = event;
			}
		}
	}

	public updateCalendar(calendarId: string, title: string, birthdate: string, age: number) {
		if (!DataManager.validateCalendarUserInput(title, birthdate, age))  return false
		let calendar = this.calendars.find(c => c.id === calendarId)
		if (!calendar) {
			showError('Calendar not found')
			return false
		}
		calendar.events = DataManager.getShiftedEvents(calendar, birthdate, age)
		calendar.title = title
		calendar.birthdate = birthdate
		calendar.age = age
		this.saveCalendar(calendar)
		this.onCalendarChanged(calendar)
		return true
	}

    private onCalendarChanged(calendar: Calendar) {
        this.onCalendarListChanged!()
        if (this.selectedCalendarId == calendar.id) {
            this.buildDayEventsArray(calendar);
            this.onSelectedCalendarChanged!()
        }
    }

	public deleteCalendar(id: string) {
		if (this.calendars.length <= 1)  throw new Error("The last calendar cannot be deleted")

		const index = this.calendars.findIndex(it => it.id === id)
		if (index < 0) {
			showError('Calendar not found')
			return false
		}
		const [deletedCalendar] = this.calendars.splice(index, 1)
		if (this.selectedCalendarId === deletedCalendar.id) {
			const calendar = this.calendars[0]
			this.selectedCalendarId = calendar.id
			this.buildDayEventsArray(calendar)
			this.onSelectedCalendarChanged!()
		}
		this.onCalendarListChanged!()

		this.saveMainDataRecord()
		this.syncManager.deleteRecord(deletedCalendar.id)

		return true
	}

	public moveCalendar(id: string, shift: number) {
		const index = this.calendars.findIndex(c => c.id === id)
		if (index < 0) {
			showError('Calendar not found')
			return false
		}
		const newIndex = index + shift
		if (index < 0 || index >= this.calendars.length || newIndex < 0 || newIndex >= this.calendars.length)  return

		const temp = this.calendars[index]
        this.calendars[index] = this.calendars[newIndex]
        this.calendars[newIndex] = temp

		this.onCalendarListChanged!()

		this.saveMainDataRecord()
	}

	public createNewCalendar(title: string, birthdate: string, age: number): boolean {
		if (!DataManager.validateCalendarUserInput(title, birthdate, age))  return false

        const calendar = {
            id : '',
			title: title,
			birthdate: birthdate,
			age: age,
			events: []
		}
        const id = this.syncManager.createNewRecord(calendar)

		this.calendars.push(calendar)
		this.selectedCalendarId = id
		this.dayEvents = []
        this.buildDayEventsArray()
		this.onCalendarListChanged!()
		this.onSelectedCalendarChanged!()
		this.saveMainDataRecord();

		return true
	}

	public copyCalendar(sourceId: string, title: string, birthdate: string, age: number): boolean {
		if (!sourceId) return false
		if (!DataManager.validateCalendarUserInput(title, birthdate, age))  return false

		const source = this.calendars.find(c => c.id === sourceId)
		if (!source) { 
			showError('Calendar not found')
			return false
		}
		const newEvents = DataManager.getShiftedEvents(source, birthdate, age)
		const calendar = {
            id : '',
			title: title,
			birthdate: birthdate,
			age: age,
			events: newEvents
		}
		const id = this.syncManager.createNewRecord(calendar)

		this.calendars.push(calendar)
		this.selectedCalendarId = id
		this.buildDayEventsArray(calendar)
		this.onCalendarListChanged!()
		this.onSelectedCalendarChanged!()

		this.saveMainDataRecord()
		return true
	}

	private static isValidAge(age: number): boolean {
		return age >= MIN_AGE && age <= MAX_AGE
	}

	private static isValidBirthDate(birthdate: string): boolean {
		// Check format YYYY-MM-DD
		if (!/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) return false;
		const [yearStr, monthStr, dayStr] = birthdate.split('-');
		const year = Number(yearStr);
		const month = Number(monthStr);
		const day = Number(dayStr);
		// Check valid ranges
		if (year < 1900 || year > 9999) return false;
		if (month < 1 || month > 12) return false;
		if (day < 1 || day > 31) return false;
		// Construct date and check validity
		const date = new Date(birthdate);
		if (isNaN(date.getTime())) return false;
		// Check that the date parts match (to catch invalid dates like 2023-02-30)
		if (date.getUTCFullYear() !== year || date.getUTCMonth() + 1 !== month || date.getUTCDate() !== day) return false;
		return true;
	}

	private static validateCalendarUserInput(title: string, birthdate: string, age: number) {
		if (title.length > 100) {
			showWarning('Invalid Title: should not exceed 100 characters');
			return false
		}
		if (!DataManager.isValidBirthDate(birthdate)) {
			showWarning('Invalid Birth Date');
			return false
		}
		if (!DataManager.isValidAge(age)) {
			showWarning(`Invalid Age: must be in range ${MIN_AGE} .. ${MAX_AGE}`);
			return false
		}
		return true
	}

    public publishCalendar(id: string): Promise<boolean> {
        return this.syncManager.publishData(id)
    }

    public unpublishCalendar(id: string): Promise<boolean> {
        return this.syncManager.unpublishData(id)
    }

    public getPublicLink(id: string): string|null {
        const publicKey = this.syncManager.getPublicKey(id)
        return publicKey ? new URL('pub/' + publicKey, window.location.href).href : null
    }

    public isPublicLink(url: string): string {
        const match = url.match(/\/pub\/([a-zA-Z0-9]+)$/);
        return match ? match[1] : ''
    }

    public async loadPublicCalendar(publicKey: string): Promise<Calendar|null> {
        const data = await this.syncManager.loadPublicData(publicKey)
        if (!data) {
            return null
        }
        
        // Check if it's a Calendar (not MainDataRecord)
        const calendar = data as Calendar
        if (!calendar.birthdate || !calendar.age) {
            showError('Invalid calendar data')
            return null
        }

        // Clear existing calendars and set this as the only one
        this.calendars = [calendar]
        this.selectedCalendarId = calendar.id
        this.buildDayEventsArray(calendar)

        return calendar
    }

	//    ----    Event manipulation    ----

	public removeEvent(index: number): void {
		let calendar = this.getSelectedCalendarOrError()
		if (!this.removeEventInternal(calendar, index)) return
		this.onCalendarListChanged!()
		this.onSelectedCalendarChanged!()
		this.saveCalendar(calendar)
	}

	private removeEventInternal(calendar: Calendar, index: number): boolean {
		let event = this.dayEvents[index]
		if (event == null) return false

		const eventIndex = calendar.events.indexOf(event)
		if (eventIndex < 0) throw new Error('Event not found')
		calendar.events.splice(eventIndex, 1)

		for (let i = 0; i < event.length; i++) {
			this.dayEvents[event.index + i] = null
		}
		return true
	}

	public getEventForDay(dayIndex: number): LifeEvent | null {
		if (!this.dayEvents || dayIndex < 0 || dayIndex >= this.dayEvents.length) {
			return null;
		}
		return this.dayEvents[dayIndex];
	}

	public changeEvent(index: number, newIndex: number, newLength: number, color: string, description: string): void {
		const calendar = this.getSelectedCalendarOrError();
		let event = this.getEventForDay(index);

		// Clear old occupancy in dayEvents
		if (event != null)
			for (let i = 0; i < event.length; i++) {
				this.dayEvents[event.index + i] = null;
			}

		// Remove overlapping events
		for (let i = newIndex; i < newIndex + newLength; i++) {
			const other = this.dayEvents[i];
			if (other && other !== event) {
				const idx = calendar.events.indexOf(other);
				if (idx >= 0)  calendar.events.splice(idx, 1);
				else  showError(null, "Event not found")
				for (let j = other.index; j < other.index + other.length; j++) {
					this.dayEvents[j] = null;
				}
			}
		}

		// Apply new event
		if (event == null) {
			event = {
				index: newIndex,
				length: newLength,
				color: color,
				description: description
			};
			calendar.events.push(event);
		} else {
			event.index = newIndex
			event.length = newLength
			event.color = color
			event.description = description
		}
		for (let i = 0; i < newLength; i++) {
			this.dayEvents[newIndex + i] = event;
		}

		this.onCalendarListChanged!();
		this.onSelectedCalendarChanged!();
		this.saveCalendar(calendar);
	}

	private static getShiftedEvents(calendar: Calendar, birthdate: string, age: number): LifeEvent[] {
		const oldBirthdate = dayjs(calendar.birthdate, 'YYYY-MM-DD');
		const newBirthdate = dayjs(birthdate, 'YYYY-MM-DD');
		const dayDifference = newBirthdate.diff(oldBirthdate, 'day');
		
		const endDate = newBirthdate.add(age, 'year');
		const endIndex = endDate.diff(newBirthdate, 'day');
		
		const shiftedEvents: LifeEvent[] = [];
		
		for (const event of calendar.events) {
			const newIndex = event.index - dayDifference;
			if (newIndex < 0) continue;
			if (newIndex >= endIndex) continue;
			
			let newLength = event.length;
			if (newIndex + newLength > endIndex) {
				newLength = endIndex - newIndex;
			}
			
			shiftedEvents.push({
				index: newIndex,
				length: newLength,
				color: event.color,
				description: event.description
			});
		}
		
		return shiftedEvents;
	}
} 